import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building,
  ArrowLeft,
  Globe,
  Shield,
  Clock,
  Save,
  AlertTriangle,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { organisationApi } from "../../services/api";

export default function OrganizationSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    website: "",
    industry: "",
    size: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    settings: {
      defaultTimezone: "Australia/Sydney",
      dateFormat: "DD/MM/YYYY",
      pageSize: "a4",
      jurisdiction: "au",
      requireMFA: false,
      sessionTimeout: 30,
      allowedDomains: "",
    },
  });

  // Fetch organisation
  const { data: org, isLoading } = useQuery({
    queryKey: ["organisation"],
    queryFn: () => organisationApi.get().then((res) => res.data?.data),
  });

  useEffect(() => {
    if (org) {
      setFormData({
        name: org.name || "",
        website: org.website || "",
        industry: org.industry || "",
        size: org.size || "",
        address: org.address || {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        },
        settings: {
          defaultTimezone: org.settings?.defaultTimezone || "Australia/Sydney",
          dateFormat: org.settings?.dateFormat || "DD/MM/YYYY",
          pageSize: org.settings?.pageSize || "a4",
          jurisdiction: org.settings?.jurisdiction || "au",
          requireMFA: org.settings?.requireMFA || false,
          sessionTimeout: org.settings?.sessionTimeout || 30,
          allowedDomains: org.settings?.allowedDomains?.join("\n") || "",
        },
      });
    }
  }, [org]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => organisationApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["organisation"]);
      setHasChanges(false);
      toast.success(t("settings.organisation.saved"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.organisation.saveFailed"),
      );
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
    setHasChanges(true);
  };

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [field]: value },
    }));
    setHasChanges(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      settings: {
        ...formData.settings,
        allowedDomains: formData.settings.allowedDomains
          .split("\n")
          .map((d) => d.trim())
          .filter(Boolean),
      },
    };

    updateMutation.mutate(payload);
  };

  const renderSaveButtonTitle = () => {
    if (updateMutation?.isPending) {
      return (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      );
    }
    return updateMutation?.isSuccess ? (
      <Check className="w-4 h-4" />
    ) : (
      <Save className="w-4 h-4" />
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border border-input hover:bg-accent transition-colors touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t("settings.organisation.title")}
          </h1>
          <p className="text-muted-foreground">
            Manage your organisation details and preferences
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("settings.organisation.details")}
              </h2>
              <p className="text-sm text-muted-foreground">
                Basic information about your organisation
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Organisation Name
              </label>
              <input
                aria-label="Organisation Name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              />
            </div>

            <div>
              <label
                htmlFor="website"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Website
              </label>
              <input
                aria-label="Website"
                type="url"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="industry"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Industry
                </label>
                <select
                  value={formData.industry}
                  onChange={(e) => handleChange("industry", e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                >
                  <option value="">{t("common.selectIndustry")}</option>
                  <option value="technology">
                    {t("industries.technology")}
                  </option>
                  <option value="finance">{t("industries.finance")}</option>
                  <option value="healthcare">
                    {t("industries.healthcare")}
                  </option>
                  <option value="legal">{t("industries.legal")}</option>
                  <option value="real_estate">
                    {t("industries.realEstate")}
                  </option>
                  <option value="education">{t("industries.education")}</option>
                  <option value="government">
                    {t("industries.government")}
                  </option>
                  <option value="retail">{t("industries.retail")}</option>
                  <option value="manufacturing">
                    {t("industries.manufacturing")}
                  </option>
                  <option value="other">{t("common.other")}</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="size"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Company Size
                </label>
                <select
                  value={formData.size}
                  onChange={(e) => handleChange("size", e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                >
                  <option value="">{t("common.selectSize")}</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="501-1000">501-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("settings.organisation.businessAddress")}
              </h2>
              <p className="text-sm text-muted-foreground">
                Your organisation's physical address
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            <div>
              <label
                htmlFor="stret"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Street Address
              </label>
              <input
                aria-label="Street Address"
                type="text"
                value={formData.address.street}
                onChange={(e) => handleAddressChange("street", e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  City
                </label>
                <input
                  aria-label="City"
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                />
              </div>
              <div>
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  State / Province
                </label>
                <input
                  aria-label="State / Province"
                  type="text"
                  value={formData.address.state}
                  onChange={(e) => handleAddressChange("state", e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="postalCode"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Postal Code
                </label>
                <input
                  aria-label="Postal Code"
                  type="text"
                  value={formData.address.postalCode}
                  onChange={(e) =>
                    handleAddressChange("postalCode", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                />
              </div>
              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  Country
                </label>
                <select
                  value={formData.address.country}
                  onChange={(e) =>
                    handleAddressChange("country", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                >
                  <option value="">{t("common.selectCountry")}</option>
                  <option value="US">{t("countries.us")}</option>
                  <option value="CA">{t("countries.ca")}</option>
                  <option value="UK">{t("countries.uk")}</option>
                  <option value="AU">{t("countries.au")}</option>
                  <option value="DE">{t("countries.de")}</option>
                  <option value="FR">{t("countries.fr")}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("settings.security.title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                Configure security options for your organisation
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <label
              htmlFor="requireMFA"
              className="flex items-center justify-between p-4 bg-muted rounded-lg cursor-pointer"
            >
              <div>
                <p className="font-medium text-foreground">
                  {t("settings.security.require2fa")}
                </p>
                <p className="text-sm text-muted-foreground">
                  All team members must enable 2FA
                </p>
              </div>
              <input
                aria-label="Require 2FA"
                type="checkbox"
                checked={formData.settings.requireMFA}
                onChange={(e) =>
                  handleSettingsChange("requireMFA", e.target.checked)
                }
                className="rounded border-input text-primary"
              />
            </label>

            <div>
              <label
                htmlFor="sessionTimeout"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Session Timeout (minutes)
              </label>
              <select
                value={formData.settings.sessionTimeout}
                onChange={(e) =>
                  handleSettingsChange(
                    "sessionTimeout",
                    parseInt(e.target.value),
                  )
                }
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={480}>8 hours</option>
              </select>
            </div>

            <div>
              <p className="block text-sm font-medium text-foreground mb-1">
                IP Restrictions
              </p>
              <p className="text-xs text-muted-foreground">
                IP access control is configured under{" "}
                <Link
                  to="/settings/advanced-security"
                  className="text-primary hover:underline"
                >
                  Advanced Security
                </Link>
                , where you can manage allowed addresses and ranges.
              </p>
            </div>

            <div>
              <label
                htmlFor="allowedDomains"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Allowed Email Domains
              </label>
              <textarea
                aria-label="Allowed Email Domains"
                value={formData.settings.allowedDomains}
                onChange={(e) =>
                  handleSettingsChange("allowedDomains", e.target.value)
                }
                placeholder="example.com&#10;company.org"
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-lg font-mono text-sm bg-background text-foreground"
              />
              <p className="text-xs text-muted-foreground mt-1">
                One domain per line. Team invitations can only be sent to
                these domains, and subdomains of them. Leave empty to allow
                any email address.
              </p>
            </div>
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t("settings.preferences.title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                Default settings for your organisation
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="defaultTimezone"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Default Timezone
              </label>
              <select
                value={formData.settings.defaultTimezone}
                onChange={(e) =>
                  handleSettingsChange("defaultTimezone", e.target.value)
                }
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              >
                <option value="Australia/Sydney">
                  {t("timezones.sydney")}
                </option>
                <option value="Australia/Melbourne">
                  {t("timezones.melbourne")}
                </option>
                <option value="Australia/Brisbane">
                  {t("timezones.brisbane")}
                </option>
                <option value="Australia/Perth">{t("timezones.perth")}</option>
                <option value="Pacific/Auckland">
                  {t("timezones.auckland")}
                </option>
                <option value="UTC">{t("common.utc")}</option>
                <option value="America/New_York">
                  {t("timezones.eastern")}
                </option>
                <option value="America/Chicago">
                  {t("timezones.central")}
                </option>
                <option value="America/Denver">
                  {t("timezones.mountain")}
                </option>
                <option value="America/Los_Angeles">
                  {t("timezones.pacific")}
                </option>
                <option value="Europe/London">{t("timezones.london")}</option>
                <option value="Europe/Paris">{t("timezones.paris")}</option>
                <option value="Asia/Tokyo">{t("timezones.tokyo")}</option>
                <option value="Asia/Singapore">
                  {t("timezones.singapore")}
                </option>
              </select>
            </div>

            <div>
              <label
                htmlFor="dateFormat"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Date Format
              </label>
              <select
                value={formData.settings.dateFormat}
                onChange={(e) =>
                  handleSettingsChange("dateFormat", e.target.value)
                }
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="pageSize"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Certificate Page Size
              </label>
              <select
                value={formData.settings.pageSize}
                onChange={(e) =>
                  handleSettingsChange("pageSize", e.target.value)
                }
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              >
                <option value="a4">A4 (210mm × 297mm)</option>
                <option value="letter">US Letter (8.5" × 11")</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Page size for certificates and audit trail documents
              </p>
            </div>

            <div>
              <label
                htmlFor="jurisdiction"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Legal Jurisdiction
              </label>
              <select
                value={formData.settings.jurisdiction}
                onChange={(e) =>
                  handleSettingsChange("jurisdiction", e.target.value)
                }
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              >
                <option value="au">🇦🇺 Australia (ETA 1999)</option>
                <option value="nz">🇳🇿 New Zealand (ETA 2002)</option>
                <option value="sg">🇸🇬 Singapore (ETA Cap 88)</option>
                <option value="us">🇺🇸 United States (ESIGN Act & UETA)</option>
                <option value="eu">🇪🇺 European Union (eIDAS)</option>
                <option value="uk">🇬🇧 United Kingdom (ECA 2000)</option>
                <option value="ca">🇨🇦 Canada (PIPEDA)</option>
                <option value="in">🇮🇳 India (IT Act 2000)</option>
                <option value="jp">🇯🇵 Japan (e-Signature Act)</option>
                <option value="br">🇧🇷 Brazil (MP 2.200-2)</option>
                <option value="mx">🇲🇽 Mexico (Commercial Code)</option>
                <option value="international">
                  🌍 International (UNCITRAL)
                </option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Legal framework for electronic signature disclaimers on
                certificates
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between">
          {hasChanges && (
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">{t("common.unsavedChanges")}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={!hasChanges || updateMutation.isPending}
            className={`ml-auto px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 ${isMobile ? "w-full justify-center h-11" : ""}`}
          >
            {renderSaveButtonTitle()}
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}
